import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Clock, Package, MapPin, Play, Plus } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useBulkPickingStore } from '../stores/wavePickingStore';
import { useAppStore } from '../stores/appStore';
import type { WavePickList } from '../types';

export const BulkPickingPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    availableWaves, 
    currentWave, 
    optimizedRoute,
    loadAvailableWaves,
    startWavePicking,
    optimizeWaveRoute,
    setCurrentWave,
    isLoading 
  } = useBulkPickingStore();
  const { addNotification } = useAppStore();

  useEffect(() => {
    loadAvailableWaves();
  }, []);

  const handleStartWave = async (wave: WavePickList) => {
    try {
      await startWavePicking(wave.id);
      await optimizeWaveRoute(wave.id);
      
      addNotification({
        type: 'success',
        title: 'Wave Started',
        message: `Wave ${wave.waveNumber} is now in progress`,
        priority: 'medium'
      });
      
      navigate(`/wave-picking/${wave.id}`);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to start wave picking',
        priority: 'high'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'neutral';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      default: return 'neutral';
    }
  };

  const getTemperatureColor = (zone?: string) => {
    switch (zone) {
      case 'frozen': return 'text-blue-600 bg-blue-100';
      case 'chilled': return 'text-green-600 bg-green-100';
      case 'ambient': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading wave picking..." />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 pb-20 sm:pb-24 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            icon={<ArrowLeft className="w-4 h-4" />}
          />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Bulk Picking
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Batch multiple orders for efficient picking
            </p>
          </div>
        </div>
      </motion.div>

      {/* Current Wave */}
      {currentWave && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Current Wave: {currentWave.waveNumber}
              </h2>
              <Badge variant={getStatusColor(currentWave.status)}>
                {currentWave.status.replace('_', ' ')}
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{currentWave.totalItems}</p>
                <p className="text-sm text-gray-600">Total Items</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{currentWave.pickedItems}</p>
                <p className="text-sm text-gray-600">Picked</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{currentWave.assignedPickers.length}</p>
                <p className="text-sm text-gray-600">Pickers</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{currentWave.estimatedTime}m</p>
                <p className="text-sm text-gray-600">Est. Time</p>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentWave.pickedItems / currentWave.totalItems) * 100}%` }}
                className="bg-blue-600 h-3 rounded-full"
              />
            </div>
            
            <Button
              onClick={() => navigate(`/wave-picking/${currentWave.id}`)}
              className="w-full"
            >
              Continue Wave
            </Button>
          </Card>
        </motion.div>
      )}

      {/* Available Waves */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Available Bulk Orders
          </h2>
          <button
            onClick={() => navigate('/create-bulk')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>+ Bulk Pick</span>
          </button>
        </div>

        {availableWaves.length === 0 ? (
          <Card className="text-center py-8">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No bulk orders available
            </h3>
            <p className="text-gray-600 mb-4">
              Create a new bulk order to batch multiple orders together.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {availableWaves.map((wave) => (
              <Card key={wave.id} hover>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">Bulk Order {wave.waveNumber}</h3>
                      <p className="text-sm text-gray-600">
                        {wave.orders.length} orders • {wave.totalItems} items
                      </p>
                      
                      {wave.temperatureZone && (
                        <div className="flex items-center space-x-1 mt-1">
                          <span className={`
                            text-xs px-2 py-1 rounded-full font-medium
                            ${getTemperatureColor(wave.temperatureZone)}
                          `}>
                            {wave.temperatureZone}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Badge variant={getStatusColor(wave.status)}>
                      {wave.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{wave.assignedPickers.length} pickers</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{wave.estimatedTime}min</span>
                    </div>
                  </div>
                </div>
                
                {wave.status === 'pending' && (
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleStartWave(wave)}
                      className="flex-1"
                      icon={<Play className="w-4 h-4" />}
                    >
                      Start Bulk
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/bulk-details/${wave.id}`)}
                    >
                      Details
                    </Button>
                  </div>
                )}
                
                {wave.status === 'in_progress' && (
                  <Button
                    onClick={() => navigate(`/bulk-picking/${wave.id}`)}
                    className="w-full"
                  >
                    Continue Picking
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};